import { z } from 'zod';
const HEX_RE = /^[0-9a-f]+$/;
const GONKA_ADDRESS_RE = /^gonka1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{38}$/;
const GONKA_HD_PATH = "m/44'/1200'/0'/0/0";
const hexString = (byteLength) => z
    .string()
    .length(byteLength * 2, `must be ${byteLength * 2} hex characters`)
    .regex(HEX_RE, 'must be lowercase hex');
const KdfParamsSchema = z.object({
    m: z.number().int().positive(),
    t: z.number().int().positive(),
    p: z.number().int().positive(),
});
/**
 * Optional WebAuthn PRF biometric unlock binding.
 *
 * - `credentialId` is the public WebAuthn credential id (base64url).
 * - `prfSalt` is a random 32-byte input we feed to the PRF extension on
 *   every authentication so the same passkey returns the same secret
 *   for *this wallet*. Different wallets stored on the same device get
 *   different prfSalts and therefore different keys.
 * - `wrappedKey` is the Argon2id-derived seed-encryption key (32 bytes)
 *   AES-256-GCM-encrypted under a key derived from the PRF output.
 *   Format: 12-byte nonce || ciphertext+tag, hex-encoded.
 *
 * The PIN-based encryption path stays unchanged. Biometric unlock is
 * an *additional* recovery path that wraps the same derived key.
 */
const BiometricWrapSchema = z.object({
    credentialId: z
        .string()
        .min(1)
        .regex(/^[A-Za-z0-9_-]+$/, 'credentialId must be base64url'),
    prfSalt: hexString(32),
    wrappedKey: z
        .string()
        .min(56, 'wrappedKey must be at least 28 bytes hex (12-byte nonce + 16-byte tag)')
        .regex(HEX_RE, 'wrappedKey must be lowercase hex'),
});
export const EncryptedWalletBlobSchema = z.object({
    version: z.literal(1),
    id: z.string().uuid(),
    name: z.string().min(1).max(64),
    kdf: z.literal('argon2id'),
    kdfParams: KdfParamsSchema,
    salt: hexString(16),
    nonce: hexString(24),
    ciphertext: z
        .string()
        .min(32, 'ciphertext must be at least 32 hex characters')
        .regex(HEX_RE, 'ciphertext must be lowercase hex'),
    hdPath: z.literal(GONKA_HD_PATH),
    addresses: z.array(z.string().regex(GONKA_ADDRESS_RE, 'invalid Gonka bech32 address')).min(1),
    createdAt: z.string().datetime(),
    biometricWrap: BiometricWrapSchema.optional(),
});
export function parseEncryptedWalletBlob(data) {
    return EncryptedWalletBlobSchema.parse(data);
}
// ────────────────────────────────────────────────────────────────────
// Phase F: watch-only addresses
// ────────────────────────────────────────────────────────────────────
/**
 * A read-only Gonka address the user wants to monitor without holding
 * the private key. Has no crypto material, no hd path, no biometric
 * wrap — only a display name and the bech32 address.
 */
export const WatchedAddressSchema = z.object({
    version: z.literal(1),
    id: z.string().uuid(),
    name: z.string().min(1).max(64),
    address: z.string().regex(GONKA_ADDRESS_RE, 'invalid Gonka bech32 address'),
    createdAt: z.string().datetime(),
});
export function parseWatchedAddress(data) {
    return WatchedAddressSchema.parse(data);
}
//# sourceMappingURL=schema.js.map